import {
  BasedFinder,
  BasedFinderHeaderProps,
  BasedFinderProps,
} from '../BasedFinder/index.js'
import { IconProps } from '../Icon/index.js'
import { Editor } from './Editor.js'
import { Card } from './Card.js'
import { createElement, ReactNode } from 'react'
import { Button } from '../Button/index.js'
import { useToast } from '../Toast/index.js'
import { capitalize, normalize } from '../../utils/text.js'
import { ContentEditorProps } from '../ContentEditor/index.js'
import { useCmsParams } from '../../hooks/useCmsParams.js'
import { useClient } from '@based/sdk/react'
import { useBasedEndpoints } from '../../providers/Endpoints/index.js'

export const Finder: React.FC<
  {
    children?:
      | ReactNode
      | ((props: {
          type: string
          section: string
          name?: string
          icon?: IconProps['variant']
          counter?: number
          id: number
        }) => ReactNode)
    section: string
    counter?: number
    disabled?: boolean
    exclude?: string[]
    include?: string[]
    transformFields?: ContentEditorProps['transformFields']
    onSubmit?: ContentEditorProps['onSubmit']
    onChange?: ContentEditorProps['onSubmit']
  } & Partial<BasedFinderProps> &
    Partial<BasedFinderHeaderProps>
> = ({
  section,
  schemaType,
  counter,
  title,
  icon,
  leadButton,
  trailButton,
  filterButton,
  sortButton,
  searchButton,
  viewSettingsButton,
  disabled,
  exclude,
  include,
  children = Editor,
  transformFields,
  onSubmit,
  onChange,
  ...props
}) => {
  const [params, go] = useCmsParams()
  const toast = useToast()
  const endpoints = useBasedEndpoints()
  const client = useClient()

  if (params.id) {
    return typeof children === 'function'
      ? createElement(children, {
          schemaType,
          section,
          icon,
          counter,
          id: params.id,
          exclude,
          include,
          transformFields,
          onSubmit,
          onChange,
        })
      : children
  }

  if (params.section && params.page && !params.id) {
    return typeof children === 'function'
      ? createElement(children, {
          schemaType,
          section,
          icon,
          counter,
          id: null,
          exclude,
          include,
          transformFields,
          onSubmit,
          onChange,
        })
      : children
  }

  // add new
  const callToAction = async () => {
    try {
      const id = await client.call(endpoints.create, {
        type: schemaType,
      })

      go({ section: normalize(section), page: 'create', id })
    } catch (err: any) {
      toast('Error', {
        color: 'red',
        description: err.message || 'Unknown error',
      })
    }
  }

  return (
    <Card>
      <BasedFinder
        schemaType={schemaType}
        onRowClick={({ id }) => {
          go({ section: normalize(section), page: 'edit', id })
        }}
        onAddNewItem={callToAction}
        {...props}
      >
        <BasedFinder.Header
          leadButton={
            leadButton === null
              ? null
              : leadButton !== undefined
                ? leadButton
                : null
          }
          title={title || capitalize(section)}
          icon={icon}
          filterButton={filterButton}
          sortButton={sortButton}
          searchButton={searchButton}
          viewSettingsButton={viewSettingsButton}
          trailButton={
            trailButton === null ? null : trailButton !== undefined ? (
              trailButton
            ) : (
              <Button
                variant="border"
                leadIcon="add"
                disabled={disabled}
                onClick={callToAction}
              >
                Add {schemaType}
              </Button>
            )
          }
        />
        <BasedFinder.Body />
      </BasedFinder>
    </Card>
  )
}
